import { useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { type App, getAnotherAppOrigin } from '@realadvisor/host-resolver';
import { encodeGlobalId } from '@realadvisor/transform/global-id';
import {
  Award,
  Building2,
  Eye,
  type LucideIcon,
  Star,
  Trash2,
  UserPen,
  Users,
} from 'lucide-react';
import { Navigate } from 'react-router-dom';

import { ActivityBanner } from '@modules/_shared/components/ActivityBanner';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { useStartTrial } from '@modules/_shared/hooks/useStartTrial';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { PaidProfileCard } from '@modules/claim/PaidProfileCard';
import type { ShowcaseEntity } from '@modules/claim/PlacementMockups';
import { ProfileImpressionsChip } from '@modules/claim/ProfileImpressionsChip';
import { ProfileVisibility } from '@modules/claim/ProfileVisibility';
import { RealAdvisorLogo } from '@shared/components/RealAdvisorLogo';
import { isProduction } from '@shared/config';
import { useGatewayLanguageMenu } from '@shared/gatewayToolbar';
import { useLocale } from '@shared/hooks/locale';
import {
  extractLanguageFromUrl,
  fallbackLanguage,
} from '@shared/LocaleContext';
import { customPalette } from '@shared/theme/StyleProvider';
import { getAfterLoginDestination } from '@shared/utils/afterLoginDestination';

import {
  GET_AGENCY,
  SEND_SLACK_NOTIFICATION,
  TAKEDOWN_AGENCY,
  TAKEDOWN_AGENT,
} from './profilePageLayoutQueries';

// ─── Types ────────────────────────────────────────────────────────────────────

export type ProfileIntent = 'claim' | 'takedown';
export type ProfileTarget = 'agent' | 'agency';

interface ProfilePageLayoutProps {
  title: string;
  description: string;
  intent: ProfileIntent;
  target?: ProfileTarget;
  ctaLabel?: string;
  // For target='agent': 'claim' calls claim_agent (only valid for an unclaimed
  // scraped profile) while 'trial' calls start_trial (a normally-registered
  // agent starting their one-time free trial). Ignored when target='agency'.
  agentAction?: 'claim' | 'trial';
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const ResponsiveDivider = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  return isDesktop ? <Divider orientation="vertical" flexItem /> : <Divider />;
};

// ─── Value section ──────────────────────────────────────────────────────────────
// Value cards shown on every profile page — "what can I do once my profile is
// live?". Shared by claim and takedown (same narrative: the value at stake), with
// an agent variant and an agency/team variant.

type ValueCard = {
  icon: LucideIcon;
  title: string;
  text: string;
};

// Agent: what the agent actively does on their profile → builds trust → more business.
// Uses a function so all strings are literal t() calls — visible to the i18n scanner.
const getAgentValueCards = (t: (key: string) => string): ValueCard[] => [
  {
    icon: UserPen,
    title: t('Complete your profile'),
    text: t(
      'Add your photo, languages, specialties and the areas you cover so owners find the right expert.',
    ),
  },
  {
    icon: Star,
    title: t('Collect client reviews'),
    text: t(
      'Gather reviews from satisfied clients to build trust and stand out from other agents.',
    ),
  },
  {
    icon: Award,
    title: t('Showcase your track record'),
    text: t(
      'Add your past sales to prove your experience and local market expertise.',
    ),
  },
  {
    icon: Eye,
    title: t('Be visible and get more leads'),
    text: t(
      'Appear across RealAdvisor where owners look for an agent and turn that visibility into new business.',
    ),
  },
];

// Agency: same narrative at team level — brand, team, reputation, shared leads.
const getAgencyValueCards = (t: (key: string) => string): ValueCard[] => [
  {
    icon: Building2,
    title: t('Complete your agency profile'),
    text: t(
      'Add your logo, description, offices and services so owners recognise your brand.',
    ),
  },
  {
    icon: Users,
    title: t('Showcase your team'),
    text: t(
      'Present all your agents and their expertise on a single agency page so owners find the right contact.',
    ),
  },
  {
    icon: Star,
    title: t('Collect client reviews'),
    text: t(
      "Gather reviews from satisfied clients to build your agency's reputation and stand out.",
    ),
  },
  {
    icon: Eye,
    title: t('Generate leads for your whole team'),
    text: t(
      'Appear across RealAdvisor where owners look for an agency and turn that visibility into leads for your whole team.',
    ),
  },
];

const getValueCards = (
  t: (key: string) => string,
  isAgency: boolean,
): ValueCard[] => (isAgency ? getAgencyValueCards(t) : getAgentValueCards(t));

const ValueColumn = ({ icon: Icon, title, text }: ValueCard) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: { xs: 3, md: 4 } }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', mb: 1.5 }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(primary, 0.08),
            border: `1px solid ${alpha(primary, 0.12)}`,
            flexShrink: 0,
          }}
        >
          <Icon size={18} color={primary} />
        </Box>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Typography
        sx={{ color: 'text.secondary', fontSize: 13, lineHeight: 1.55 }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const ValueSection = ({
  t,
  isAgency,
}: {
  t: (key: string) => string;
  isAgency: boolean;
}) => {
  const valueCards = getValueCards(t, isAgency);
  return (
    // Value card — what your profile can do for you
    <Box
      sx={{
        mt: { xs: 2, md: 0 },
        bgcolor: 'background.paper',
        border: '1px solid rgba(15,17,22,0.09)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        divider={<ResponsiveDivider />}
        spacing={0}
      >
        {valueCards.map(card => (
          <ValueColumn key={card.title} {...card} />
        ))}
      </Stack>
    </Box>
  );
};

// ─── Layout ───────────────────────────────────────────────────────────────────

export const ProfilePageLayout = ({
  title,
  description,
  intent,
  target = 'agent',
  ctaLabel,
  agentAction = 'claim',
}: ProfilePageLayoutProps) => {
  const { me, noAccess, needsOnboarding } = useAppData();
  const { t } = useLocale();
  useGatewayLanguageMenu();
  const userId = me?.id ?? needsOnboarding?.userId ?? noAccess?.userId ?? '';
  const lang = me?.language ?? extractLanguageFromUrl() ?? fallbackLanguage;
  const isAgency = target === 'agency';
  const isTakedown = intent === 'takedown';
  // A pure free trial (new agent on /plans), as opposed to claiming a scraped
  // profile. Grants CRM access via start_trial and skips the claim-only Slack
  // follow-up and agency-pages revalidation (there is no profile to publish).
  const isTrial = !isAgency && agentAction === 'trial';
  const [takedownDone, setTakedownDone] = useState(false);
  const [claimFailed, setClaimFailed] = useState(false);

  // Ref allows onCompleted callbacks (defined before the guard) to call
  // sendFollowupSlack (defined after the guard) without violating hooks order.
  const followupSlackRef = useRef<
    | ((
        slug: string | null,
        intentOverride?: ProfileIntent,
      ) => Promise<unknown>)
    | null
  >(null);

  const {
    startTrial,
    claimAgent,
    loading: trialLoading,
  } = useStartTrial(
    () => {
      // The claim actions (claim_agency for an agency, claim_agent for an agent)
      // share this success handler: pass 'claim' explicitly so the Slack FOLLOWUP
      // is correct even when triggered from a takedown page (intent='takedown')
      // where the user chose to claim instead. A pure trial publishes nothing, so
      // it skips both side effects and only redirects to the dashboard.
      const redirect = () => {
        window.location.href = `/${lang}/dashboard`;
      };
      if (isTrial) {
        redirect();
        return;
      }
      // The unclaimed gate sends every URL here with `?return=` (e.g. a lead
      // link from an email): go back there once claimed, through the same
      // same-origin check as the login redirect.
      const leave = new URLSearchParams(window.location.search).has('return')
        ? () => {
            window.location.href = getAfterLoginDestination();
          }
        : redirect;
      // The Slack FOLLOWUP has no server-side fallback, and an immediate
      // navigation aborts in-flight requests: give it a bounded head start
      // before leaving the page. (Cache revalidation is handled server-side by
      // the claim/takedown action handlers.)
      void Promise.race([
        followupSlackRef.current?.(null, 'claim') ?? Promise.resolve(),
        new Promise(resolve => setTimeout(resolve, 1500)),
      ]).finally(leave);
    },
    (message: string) => {
      console.error('[claim] action failed:', message);
      setClaimFailed(true);
    },
  );

  // All mutations must be declared before any conditional return (Rules of Hooks).
  const [sendSlack] = useMutation(SEND_SLACK_NOTIFICATION);

  // Takedown mutations — both registered, only one invoked
  const [takedownAgent, { loading: agentTdLoading, error: agentTdError }] =
    useMutation(TAKEDOWN_AGENT, {
      onCompleted: () => {
        followupSlackRef.current?.(myAgentSlugRef.current);
        setTakedownDone(true);
      },
    });
  const [takedownAgency, { loading: agencyTdLoading, error: agencyTdError }] =
    useMutation(TAKEDOWN_AGENCY, {
      onCompleted: () => {
        followupSlackRef.current?.(agencySlugRef.current);
        setTakedownDone(true);
      },
    });

  // Agency data: team logo + agent ids for takedown
  const { data: agencyData, loading: agencyLoading } = useQuery(GET_AGENCY, {
    variables: { userId, tenantId: me?.tenant_id ?? '' },
    skip: !userId || !me?.tenant_id,
  });

  const team = agencyData?.teams?.[0];
  // Agent/agency slugs used in the Slack FOLLOWUP notification on takedown.
  const agencySlug = team?.agency_slug ?? null;

  // Keep mutable refs so onCompleted callbacks (closures from first render) always
  // see the latest values without needing to re-register the mutations.
  const agencySlugRef = useRef(agencySlug);
  agencySlugRef.current = agencySlug;

  // Agent's own slug for the Slack notification (agent_slug is not in ME_QUERY,
  // so it comes from GET_AGENCY data).
  const myAgentSlug =
    team?.teams_users.find(tu => tu.user.id === userId)?.user.agent_slug ??
    null;
  const myAgentSlugRef = useRef(myAgentSlug);
  myAgentSlugRef.current = myAgentSlug;

  // ── Takedown success ─────────────────────────────────────────────────────────
  // Must be checked before any loading/redirect guards: after takedown, the
  // GET_AGENCY query refetches (cache invalidated), which would otherwise
  // trigger the agencyLoading spinner or the agencyAlreadyClaimed redirect
  // and swallow the confirmation screen.

  if (takedownDone) {
    const landingUrl =
      getAnotherAppOrigin(
        window.location.origin,
        (me?.tenant?.country_code ?? 'ch').toLowerCase() as App,
      ) + `/${lang}`;
    return (
      <Box
        sx={{
          bgcolor: customPalette.gray50,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 0, md: 2 },
          py: { xs: 6, md: 10 },
        }}
      >
        <Stack spacing={2} sx={{ alignItems: 'center', maxWidth: 480 }}>
          <Alert severity="success" sx={{ width: '100%' }}>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              {isAgency
                ? t('Agency profile removed')
                : t('Your profile has been removed')}
            </Typography>
            <Typography variant="body2">
              {isAgency
                ? t(
                    'Your agency and its agent profiles have been removed from RealAdvisor.',
                  )
                : t(
                    'Your agent profile has been removed from RealAdvisor.',
                  )}{' '}
              {t(
                'It may take up to 2 hours to disappear from all RealAdvisor pages.',
              )}
            </Typography>
          </Alert>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center' }}
          >
            {t('You can safely close this page.')}
          </Typography>
          <Button
            component="a"
            href={landingUrl}
            variant="outlined"
            color="primary"
          >
            {t('Back to RealAdvisor')}
          </Button>
        </Stack>
      </Box>
    );
  }

  // Show a spinner while agency data is loading to avoid briefly rendering the
  // form before the agencyAlreadyClaimed guard has data to evaluate.
  if (isAgency && agencyLoading) {
    return (
      <LoadingSpinner
        sx={{
          minHeight: '100vh',
          p: 0,
        }}
      />
    );
  }

  // Guard: redirect if agency already claimed by another team member.
  // Applies to both takedown and claim — prevents double-claim.
  const agencyAlreadyClaimed =
    isAgency && agencyData != null && team?.is_unclaimed === false;

  if (agencyAlreadyClaimed) {
    if (me?.show_plans === 'no') {
      window.location.href = `/${lang}/dashboard`;
      return null;
    }
    if (me?.has_free_crm_access === true) {
      return <Navigate to="/plans" replace />;
    }
    return <Navigate to="/claim" replace />;
  }

  const sendFollowupSlack = (
    slug: string | null,
    intentOverride?: ProfileIntent,
  ) => {
    const effectiveIntent = intentOverride ?? intent;
    const crmUserUrl = `${
      window.location.origin
    }/${lang}/v1/users/${encodeURIComponent(
      encodeGlobalId('User', userId),
    )}/overview`;
    const lines = [
      `✅${
        effectiveIntent === 'claim'
          ? target === 'agency'
            ? '🏠'
            : '🙋'
          : target === 'agency'
          ? '🏚️'
          : '🚷'
      } *[REQUEST APPLIED]* ${effectiveIntent.toUpperCase()} · ${target.toUpperCase()}`,
      `Name: ${me?.full_name ?? userId}`,
      ...(slug != null ? [`Slug: ${slug}`] : []),
      `User ID: ${userId}`,
      ...(isAgency && team?.id != null ? [`Team ID: ${team.id}`] : []),
      `CRM: ${crmUserUrl}`,
    ];
    return sendSlack({
      variables: {
        channel: isProduction ? '#profile-requests' : '#test-n8n-notification',
        text: lines.join('\n'),
      },
    }).catch(() => undefined);
  };
  followupSlackRef.current = sendFollowupSlack;

  const takedownLoading = isAgency ? agencyTdLoading : agentTdLoading;
  const takedownError = isAgency ? agencyTdError : agentTdError;

  const handleTakedown = () => {
    if (isAgency && team != null) {
      void takedownAgency({ variables: { teamId: team.id } });
    } else {
      void takedownAgent();
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────────

  const derivedCtaLabel =
    ctaLabel ?? (isAgency ? t('Claim my agency') : t('Claim my profile'));
  const teamLogoUrl = team?.logo?.url ?? null;
  const agencyName = team?.name ?? me?.default_team?.name ?? null;
  // Who the placement mockups highlight as "You": the agent, or the agency.
  const showcaseYou: ShowcaseEntity = isAgency
    ? {
        name: agencyName ?? '',
        photo: teamLogoUrl,
        square: true,
        isYou: true,
      }
    : {
        name: me?.full_name ?? '',
        photo: me?.user_images?.[0]?.image?.url ?? null,
        isYou: true,
      };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        bgcolor: customPalette.gray50,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        px: { xs: 0, md: 2 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="lg">
        {/* Hero */}
        <Stack
          spacing={1.5}
          sx={{ alignItems: 'center', textAlign: 'center', mb: 0 }}
        >
          <RealAdvisorLogo style={{ height: 22, marginBottom: 4 }} />

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 26, sm: 30, md: 34 },
              fontWeight: 600,
              letterSpacing: '-0.01em',
              lineHeight: 1.15,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 14, md: 15 },
              color: 'text.secondary',
              maxWidth: 680,
              lineHeight: 1.55,
            }}
          >
            {description}
          </Typography>

          {!isTrial && (
            <ProfileImpressionsChip
              isAgency={isAgency}
              teamId={team?.id ?? null}
              userId={userId}
              tenantId={me?.tenant_id}
            />
          )}

          {userId !== '' && <ActivityBanner userId={userId} />}

          {/* Hero CTA — overlaps the advantages card */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: '66.67%' },
              transform: { xs: 'none', md: 'translateY(16px)' },
              zIndex: 1,
            }}
          >
            <Stack
              direction="row"
              spacing={2.5}
              useFlexGap
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                px: 2,
                py: 1.25,
                bgcolor: customPalette.blue50,
                border: '1px solid rgba(82,120,223,0.15)',
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                width: '100%',
              }}
            >
              {me != null && (
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: 'center', flex: '1 1 auto', minWidth: 0 }}
                >
                  <Box sx={{ display: 'flex', flexShrink: 0 }}>
                    <Avatar
                      src={me.user_images?.[0]?.image?.url ?? undefined}
                      alt={me.full_name ?? ''}
                      sx={{
                        width: 48,
                        height: 48,
                        ...(isAgency && {
                          border: `2px solid ${customPalette.white}`,
                          zIndex: 2,
                        }),
                      }}
                    />
                    {isAgency && (
                      <Avatar
                        src={teamLogoUrl ?? undefined}
                        alt={me.default_team?.name ?? ''}
                        sx={{
                          width: 48,
                          height: 48,
                          border: `2px solid ${customPalette.white}`,
                          ml: -1.5,
                          zIndex: 1,
                          bgcolor: 'grey.100',
                        }}
                      >
                        <Building2 size={20} />
                      </Avatar>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                      {me.full_name}
                    </Typography>
                    {me.default_team?.name != null && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{ alignItems: 'center' }}
                      >
                        <Building2
                          size={13}
                          style={{
                            color: customPalette.gray500,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          {me.default_team.name}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}

              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  setClaimFailed(false);
                  if (isAgency) {
                    void startTrial(team?.id);
                  } else if (isTrial) {
                    void startTrial();
                  } else {
                    void claimAgent();
                  }
                }}
                disabled={!userId || trialLoading || (isAgency && team == null)}
                sx={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {trialLoading && (
                  <CircularProgress
                    size={18}
                    sx={{ mr: 1, color: 'inherit' }}
                  />
                )}
                {derivedCtaLabel}
              </Button>
              {claimFailed && (
                <Typography variant="caption" color="error">
                  {t('Something went wrong. Please try again.')}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Claim and takedown: where homeowners see the profile (on takedown,
            the visibility the user is about to give up). A trial has no
            profile yet: it keeps the value cards. */}
        {isTrial ? (
          <ValueSection t={t} isAgency={isAgency} />
        ) : (
          <>
            <ProfileVisibility
              isAgency={isAgency}
              countryCode={me?.tenant?.country_code}
              you={showcaseYou}
            />
            <PaidProfileCard
              userId={userId}
              page={isTakedown ? 'takedown' : 'claim'}
              isAgency={isAgency}
              countryCode={me?.tenant?.country_code}
              agentName={me?.full_name ?? ''}
              agentEmail={
                me?.emails?.find(email => email.primary)?.email ??
                me?.emails?.[0]?.email ??
                null
              }
              agencyName={agencyName}
            />
          </>
        )}

        {/* Takedown footer */}
        {isTakedown && (
          <Box sx={{ mt: 2 }}>
            <Stack
              direction="row"
              spacing={2}
              useFlexGap
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                px: { xs: 3, md: 4 },
                py: 2,
                bgcolor: customPalette.red50,
                border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: '8px',
              }}
            >
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography
                  variant="body2"
                  color="error.main"
                  sx={{ fontWeight: 500, lineHeight: 1.4 }}
                >
                  {isAgency
                    ? t(
                        'Still want to remove your agency and all team members?',
                      )
                    : t('Still want to remove your profile?')}
                </Typography>
                {isAgency && team != null && (
                  <Stack direction="row" spacing={-0.75} sx={{ mt: 1 }}>
                    {team.teams_users
                      .filter(tu => tu.user.show_in_agency_pages)
                      .map(tu => (
                        <Avatar
                          key={tu.user.id}
                          src={
                            tu.user.user_images?.[0]?.image?.url ?? undefined
                          }
                          alt={tu.user.full_name ?? ''}
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: 10,
                            border: `1.5px solid ${customPalette.red50}`,
                          }}
                        />
                      ))}
                  </Stack>
                )}
                {takedownError != null && (
                  <Typography variant="caption" color="error">
                    {t('Something went wrong. Please try again.')}
                  </Typography>
                )}
              </Box>
              <LoadingButton
                variant="outlined"
                color="error"
                size="small"
                loading={takedownLoading}
                disabled={isAgency && team == null}
                startIcon={<Trash2 />}
                onClick={handleTakedown}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {isAgency ? t('Delete my agency') : t('Delete my profile')}
              </LoadingButton>
            </Stack>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            color: 'text.disabled',
            mt: 3,
          }}
        >
          {t('For additional information, contact')}{' '}
          <Box
            component="a"
            href="mailto:support@realadvisor.com"
            sx={{ color: 'inherit', textDecoration: 'underline' }}
          >
            support@realadvisor.com
          </Box>
        </Typography>
      </Container>
    </Box>
  );
};
